import { decodePcm16Wav, type PcmAudio } from './menu-pcm';

export type MenuCueName = 'hover' | 'confirm' | 'start';

export interface MenuCueDefinition {
  url: string;
  volume: number;
}

export interface MenuCueEngine {
  available(): boolean;
  preload(): void;
  unlock(): void;
  play(name: MenuCueName): boolean;
  stop(finishStartCue?: boolean): void;
  dispose(): void;
}

export interface MenuCueEngineOptions {
  doc: Document;
  cues: Record<MenuCueName, MenuCueDefinition>;
}

/**
 * Fetch and parse the repository's PCM16 cues before interaction without
 * opening audio hardware. The live destination and AudioBuffers are created
 * only by a real gesture and are disposed with the menu.
 */
export function createMenuCueEngine({ doc, cues }: MenuCueEngineOptions): MenuCueEngine {
  const ViewAudioContext = doc.defaultView?.AudioContext;
  let context: AudioContext | null = null;
  let contextUnavailable = false;
  const buffers = new Map<MenuCueName, PcmAudio>();
  const audioBuffers = new Map<MenuCueName, AudioBuffer>();
  const inflight = new Map<MenuCueName, Promise<PcmAudio>>();
  const active = new Map<MenuCueName, { source: AudioBufferSourceNode; gain: GainNode }>();
  const pending = new Map<MenuCueName, symbol>();
  let disposed = false;
  let unlockRequested = false;

  const ensureContext = () => {
    if (context || contextUnavailable || disposed || !ViewAudioContext) return context;
    try {
      context = new ViewAudioContext({ latencyHint: 'interactive' });
    } catch {
      contextUnavailable = true;
    }
    return context;
  };

  const load = (name: MenuCueName): Promise<PcmAudio> | null => {
    if (disposed) return null;
    const cached = buffers.get(name);
    if (cached) return Promise.resolve(cached);
    const pending = inflight.get(name);
    if (pending) return pending;
    const task = fetch(cues[name].url)
      .then((response) => {
        if (!response.ok) throw new Error(`Menu cue fetch ${response.status}: ${cues[name].url}`);
        return response.arrayBuffer();
      })
      .then(decodePcm16Wav)
      .then((buffer) => {
        if (!disposed) buffers.set(name, buffer);
        return buffer;
      });
    inflight.set(name, task);
    void task
      .finally(() => {
        if (inflight.get(name) === task) inflight.delete(name);
      })
      .catch(() => {});
    return task;
  };

  const stopCue = (name: MenuCueName) => {
    const voice = active.get(name);
    if (!voice) return;
    active.delete(name);
    voice.source.onended = null;
    try {
      voice.source.stop();
    } catch {
      // Already finished between the map lookup and stop().
    }
    voice.source.disconnect();
    voice.gain.disconnect();
  };
  const start = (name: MenuCueName, token: symbol, pcm: PcmAudio) => {
    if (!context || disposed || context.state !== 'running') return;
    if (pending.get(name) !== token) return;
    pending.delete(name);
    for (const other of [...active.keys()]) stopCue(other);
    pending.clear();
    const source = context.createBufferSource();
    const gain = context.createGain();
    let buffer = audioBuffers.get(name);
    if (!buffer) {
      buffer = context.createBuffer(pcm.channels.length, pcm.channels[0]!.length, pcm.sampleRate);
      pcm.channels.forEach((channel, index) =>
        buffer!.copyToChannel(new Float32Array(channel), index),
      );
      audioBuffers.set(name, buffer);
    }
    source.buffer = buffer;
    gain.gain.value = cues[name].volume;
    source.connect(gain).connect(context.destination);
    active.set(name, { source, gain });
    source.onended = () => {
      if (active.get(name)?.source === source) active.delete(name);
      source.disconnect();
      gain.disconnect();
    };
    source.start();
  };

  return {
    available: () => context !== null && !contextUnavailable && !disposed,
    preload() {
      for (const name of Object.keys(cues) as MenuCueName[]) void load(name)?.catch(() => {});
    },
    unlock() {
      unlockRequested = true;
      const current = ensureContext();
      if (current?.state === 'suspended') void current.resume().catch(() => {});
    },
    play(name) {
      const pcm = buffers.get(name);
      const current = context;
      if (!current || !pcm || disposed) return false;
      if (current.state !== 'running' && !unlockRequested) return false;
      pending.clear();
      const token = Symbol(name);
      pending.set(name, token);
      if (current.state === 'running') start(name, token, pcm);
      else {
        void current
          .resume()
          .then(() => start(name, token, pcm))
          .catch(() => {});
      }
      return true;
    },
    stop(finishStartCue = false) {
      for (const name of [...active.keys()]) {
        if (!finishStartCue || name !== 'start') stopCue(name);
      }
      for (const name of [...pending.keys()]) {
        if (!finishStartCue || name !== 'start') pending.delete(name);
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const name of [...active.keys()]) stopCue(name);
      buffers.clear();
      audioBuffers.clear();
      inflight.clear();
      pending.clear();
      void context?.close().catch(() => {});
      context = null;
    },
  };
}
